'use client';

import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentIc } from '../../../../components/icons';
import { IncomeTableProps, EarnData } from '../types';
import { getTableColumns } from '../constants';
import SourceCell from './SourceCell';
import DateCell from './DateCell';
import FansCell from './FansCell';
import EarningsCell from './EarningsCell';

const IncomeTable: React.FC<IncomeTableProps> = ({
  data,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t, lang } = useTranslations();

  const renderCell = useCallback((item: EarnData, columnKey: string) => {
    switch (columnKey) {
      case 'source':
        return <SourceCell data={item} />;
      case 'date':
        return <DateCell data={item} />;
      case 'fans':
        return <FansCell data={item} />;
      case 'earnings':
        return <EarningsCell data={item} />;
      default:
        return null;
    }
  }, []);

  const columns = getTableColumns(t);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      mobileSize="sm"
      key={lang}
      data={data}
      columns={columns}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('partner.revshare.income.noIncome')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('partner.revshare.income.noIncomeDescription')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default IncomeTable;
